// components/AddCardGuestsModal.js - Select guests for private card access
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiCheck, FiUser } from 'react-icons/fi';
import supabase from '../services/supabase';
import { useAuth } from '../services/auth';
import '../styles/AddCardGuestsModal.scss';

const AddCardGuestsModal = ({ visible, onClose, selectedUserIds, onUsersSelected }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (visible) {
      setSelectedIds(selectedUserIds || []);
      loadUsers();
    }
  }, [visible, selectedUserIds]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Get user's followers and following
      const { data: connections, error: connectionsError } = await supabase
        .from('followers')
        .select('follower_id, following_id')
        .or(`follower_id.eq.${user.id},following_id.eq.${user.id}`);

      if (connectionsError) throw connectionsError;

      // Extract unique user IDs
      const userIds = new Set();
      connections?.forEach(conn => {
        if (conn.follower_id !== user.id) userIds.add(conn.follower_id);
        if (conn.following_id !== user.id) userIds.add(conn.following_id);
      });

      if (userIds.size === 0) {
        setUsers([]);
        return;
      }

      // Fetch user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('users')
        .select('id, first_name, last_name, username, profile_photo_cdn_url, avatar_config')
        .in('id', Array.from(userIds))
        .order('first_name', { ascending: true });

      if (profilesError) throw profilesError;

      setUsers(profiles || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUser = (userId) => {
    setSelectedIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSave = () => {
    onUsersSelected(selectedIds);
    onClose();
  };

  const filteredUsers = users.filter(u => {
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const username = u.username?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || username.includes(query);
  });

  const getUserName = (user) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'User';
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="add-card-guests-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="modal-header">
            <h3>Add People</h3>
            <button className="btn-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          {/* Search */}
          <div className="search-section">
            <div className="search-input">
              <FiSearch />
              <input
                type="text"
                placeholder="Search by name or username"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Selected Count */}
          {selectedIds.length > 0 && (
            <div className="selected-count">
              {selectedIds.length} {selectedIds.length === 1 ? 'person' : 'people'} selected
            </div>
          )}

          {/* Users List */}
          <div className="users-list">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading connections...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="empty-state">
                {searchQuery ? (
                  <>
                    <FiSearch className="empty-icon" />
                    <p>No users found matching "{searchQuery}"</p>
                  </>
                ) : (
                  <>
                    <FiUser className="empty-icon" />
                    <p>No connections found</p>
                    <small>Follow users to add them to your private cards</small>
                  </>
                )}
              </div>
            ) : (
              filteredUsers.map(u => (
                <button
                  key={u.id}
                  className={`user-item ${selectedIds.includes(u.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleUser(u.id)}
                >
                  <div className="user-info">
                    {u.profile_photo_cdn_url ? (
                      <img src={u.profile_photo_cdn_url} alt={getUserName(u)} className="user-avatar" />
                    ) : (
                      <div className="user-avatar-placeholder">
                        {getUserName(u).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="user-details">
                      <div className="user-name">{getUserName(u)}</div>
                      {u.username && <div className="user-username">@{u.username}</div>}
                    </div>
                  </div>
                  <div className={`checkbox ${selectedIds.includes(u.id) ? 'checked' : ''}`}>
                    {selectedIds.includes(u.id) && <FiCheck />}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Done
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddCardGuestsModal;
